import type { RudderAnalytics } from '@rudderstack/analytics-js';

import {
  getClientTrackingProperties,
  getTrackingPropertiesForEvent,
} from '@/utils/clientIdentity';

import type { TrackingInterface } from '../_types';

// SQR-13012: constructed via dynamic import inside initialize() so the SDK
// (~109KB) is a split async chunk, fetched at idle rather than in the entry
// bundle. The `import type` above is erased at compile time; only the dynamic
// import() inside initialize() pulls the SDK. Pre-init events are held by the
// TrackingHandler queue, so the old "SDK buffers pre-load calls" behavior is
// preserved end-to-end.
let rudderClient: RudderAnalytics | null = null;

export function rudderstackTracker(
  writeKey: string,
  dataPlaneUrl: string,
): TrackingInterface & {
  getAnonymousUserId: () => Promise<string | undefined>;
} {
  const initialize = async () => {
    if (rudderClient) {
      return;
    }
    const { RudderAnalytics } = await import('@rudderstack/analytics-js');
    rudderClient = new RudderAnalytics();
    rudderClient.load(writeKey, dataPlaneUrl);
  };

  const setUserId: TrackingInterface['setUserId'] = (userId, context) => {
    if (!rudderClient) {
      return;
    }
    rudderClient.identify(userId, context ?? {}, {});
  };

  const trackScreenName: TrackingInterface['trackScreenName'] = (
    name,
    params,
  ) => {
    if (!rudderClient) {
      return;
    }
    rudderClient.page(name, { ...getClientTrackingProperties(), ...params });
  };

  const track: TrackingInterface['track'] = (message, context) => {
    if (!rudderClient) {
      return;
    }
    rudderClient.track(message, {
      ...getTrackingPropertiesForEvent(message),
      ...context,
    });
  };

  const getAnonymousUserId = async () => {
    if (!rudderClient) {
      return undefined;
    }

    let anonymousId = rudderClient.getAnonymousId();

    if (!anonymousId) {
      anonymousId = crypto.randomUUID();
      rudderClient.setAnonymousId(anonymousId);
    }

    return anonymousId as string | undefined;
  };

  return Object.freeze({
    initialize,
    setUserId,
    trackScreenName,
    track,
    getAnonymousUserId,
  });
}
